import type { CartSecretCodes } from '~/types';
import { type InjectionKey } from 'vue';
import { CouponCodeSecrets } from '~/constants';

export const CartSecretCodesKey: InjectionKey<CartSecretCodes> = Symbol.for('CartSecretCodes');

// The codes map is created per Nuxt app instance: per request on the server,
// once per session on the client (so state still carries from cart to
// checkout). A module-scope map would be shared by every SSR request for the
// lifetime of the server process and bleed between users.
export function useCartSecretCodesState(): CartSecretCodes {
    const nuxtApp = useNuxtApp() as unknown as { _cartSecretCodes?: CartSecretCodes };

    if (!nuxtApp._cartSecretCodes) {
        const populatedSecretCodes: CartSecretCodes = new Map();
        CouponCodeSecrets.map((code) => populatedSecretCodes.set(code, null));

        const cartSecretCodes = reactive<CartSecretCodes>(populatedSecretCodes);

        cartSecretCodes.clear = function(): void {
            this.forEach((_, key) => this.set(key, null));
        };

        nuxtApp._cartSecretCodes = cartSecretCodes;
    }

    return nuxtApp._cartSecretCodes;
}

export function useCartSecretCodes() {
    const createCartSecretCodes = inject<CartSecretCodes>(CartSecretCodesKey);

    if (!createCartSecretCodes) {
        throw new Error('Could not resolve Cart Secret Code provider');
    }

    return {
        cartSecretCodes: createCartSecretCodes,
        clear: createCartSecretCodes.clear
    };
}
